import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useNativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import ClubVerification from '@Domains/Cart/Feature/DiscountCodes/ClubVerification'
import { BarContentFragment, useSetDiscountCodeInCartMutation } from '@Generated/graphql'

interface Data {
  apply: (code: string) => Promise<{ errorMessage?: string, success: boolean }>
  bars: BarContentFragment[]
  close: () => void
}

const context = createContext<Data>({
  apply: async () => ({ success: false }),
  bars: [],
  close: () => {},
})

export const DiscountCodesProvider = ({ children }: { children: ReactNode }) => {
  const clubVerificationModalActions = useNativeModalActions()
  const [bars, setBars] = useState<BarContentFragment[]>([])
  const [setDiscountCodeInCart] = useSetDiscountCodeInCartMutation({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
  })
  const [previousDiscountCode, setPreviousDiscountCode] = useState<string | undefined>()

  const apply = useCallback(async (code: string, customerID?: string) => {
    setBars([])

    try {
      const { data } = await setDiscountCodeInCart({ variables: { customerID, discountCode: code } })

      if (!data?.setDiscountCodeInCart?.success) {
        if (data?.setDiscountCodeInCart?.prompts
          && data?.setDiscountCodeInCart?.prompts?.length !== 0
          && data?.setDiscountCodeInCart?.prompts?.[0]?.__typename === 'SetDiscountCodeInCartPromptError'
        ) {
          if (data?.setDiscountCodeInCart?.prompts?.[0]?.code === 'missing_customer_id') {
            setPreviousDiscountCode(code)
            clubVerificationModalActions.current?.open()

            // return no success and no error message, to prevent others from rendering any happy/unhappy path, as we handle it here
            return {
              success: false,
              errorMessage: undefined,
            }
          }
          return {
            success: false,
            errorMessage: data?.setDiscountCodeInCart?.prompts?.[0]?.message || 'missing error message',
          }
        }

        return {
          success: false,
          errorMessage: 'missing error prompt',
        }
      }

      if (data?.setDiscountCodeInCart?.prompts
        && data?.setDiscountCodeInCart?.prompts?.length !== 0
        && data?.setDiscountCodeInCart?.prompts?.[0]?.__typename === 'SetDiscountCodeInCartPromptSuccess'
      ) {
        setBars(data?.setDiscountCodeInCart?.prompts?.[0]?.bars || [])
      }

      return {
        success: true,
        errorMessage: undefined,
      }
    } catch (err) {
      return {
        success: false,
        errorMessage: 'unknown error',
      }
    }
  }, [clubVerificationModalActions, setBars, setDiscountCodeInCart])

  const close = useCallback(() => setBars([]), [setBars])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const searchParams = new URLSearchParams(window.location.search)
    const voucherCode = searchParams.get('thcode') || undefined
    if (!voucherCode) {
      return
    }

    const customerID = searchParams.get('thcid') || undefined
    apply(voucherCode, customerID)
  }, [apply])

  return (
    <context.Provider value={useMemo(() => ({ apply, bars, close }), [apply, bars, close])}>
      {children}
      <ClubVerification
        actionRef={clubVerificationModalActions}
        apply={(customerID: string) => apply(previousDiscountCode || '', customerID)}
      />
    </context.Provider>
  )
}

export function useDiscountCodes() {
  return useContext(context)
}
